import LocalizedLink from "@/components/LocalizedLink";
import ArticleCover from "@/components/ArticleCover";
import NextArrow from "@/components/NextArrow";
import SaveButton from "@/components/SaveButton";
import { useArticles } from "@/content/ContentProvider";
import { pickLang, useLanguage } from "@/i18n/LanguageContext";

/**
 * Article preview card.
 *
 * Wrapping element is a `<div className="relative">` so we can overlay
 * the SaveButton in the top-right of the cover without making the
 * heart a nested interactive inside the wrapping `<a>` (which is bad
 * for accessibility). The link itself spans the textual content so a
 * tap anywhere on the card body still opens the article.
 *
 * The heart is positioned with `top` + `end-3` (logical) so it stays
 * on the visually-trailing side of the cover in both LTR and RTL.
 */
export default function ArticleCard({ article, compact = false, testId }) {
  const { lang } = useLanguage();
  const { ARTICLE_CATEGORIES } = useArticles();
  const cat = ARTICLE_CATEGORIES.find((c) => c.id === article.category);
  const categoryLabel = pickLang(cat?.label, lang);
  const readLabel = lang === "ar" ? "اقرأ المقالة" : "Read the article";

  return (
    <div
      className="relative flex flex-col h-full group"
      data-testid={testId || `article-card-${article.slug}`}
    >
      {/* Save button — overlays the top-end corner of the cover.
          Outside the LocalizedLink so its click doesn't bubble into a
          navigation event. */}
      <div className="absolute top-3 end-3 z-10">
        <SaveButton
          itemType="article"
          itemSlug={article.slug}
          itemId={article.id}
          variant="card"
          testId={`article-save-${article.slug}`}
          stopPropagation={false}
        />
      </div>

      <LocalizedLink
        to={`/articles/${article.slug}`}
        className="block flex-1 flex flex-col"
        aria-label={pickLang(article.title, lang)}
      >
        <article className="flex flex-col h-full">
          <ArticleCover
            variant={article.coverVariant}
            imageUrl={article.coverUrl || ""}
            alt=""
          />

          <div className="mt-5 flex items-center gap-3 text-xs text-[color:var(--sas-ink-2)] flex-wrap">
            <span className="sas-chip">{categoryLabel}</span>
            <span aria-hidden>·</span>
            <time dateTime={article.date}>{pickLang(article.dateLabel, lang)}</time>
            {!compact && article.readingTime && (
              <>
                <span aria-hidden>·</span>
                <span>{pickLang(article.readingTime, lang)}</span>
              </>
            )}
          </div>

          <h3 className="sas-h3 mt-3 group-hover:text-[color:var(--sas-accent)] transition-colors">
            {pickLang(article.title, lang)}
          </h3>

          {!compact && (
            <p className="sas-body mt-3 text-[15px] line-clamp-3">
              {pickLang(article.excerpt, lang)}
            </p>
          )}

          <span className="sas-btn-link mt-5 inline-flex">
            {readLabel} <NextArrow size={15} />
          </span>
        </article>
      </LocalizedLink>
    </div>
  );
}
